import React from "react";
import { type Stripe } from "@stripe/stripe-js";
import Invoice from "./paymentmethods/Invoice";
import Sepa from "./paymentmethods/Sepa";
import Card from "./paymentmethods/Card";
import PaymentMethodTabButton from "./paymentmethods/PaymentMethodButton";
import { Loading } from "../../components/panels";
import { Elements } from "@stripe/react-stripe-js";
import { useMutation } from "@tanstack/react-query";
import { SomethingUnexpectedHappenedMessageBox } from "components/MessageBox/SomethingUnexpectedHappenedMessageBox";

interface Props {
  stripe: Promise<Stripe>
  config: server.dto.ClientPaymentSetupConfig
  fetchIntentApi: (type: server.dto.ClientPaymentMethodTypes) => Promise<string>
}

const StripeSetupIntentApp = (props: Props) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<server.dto.ClientPaymentMethodTypes>(undefined);
  const [hasIgnoredCardWarning, setHasIgnoredCardWarning] = React.useState(!props.config.preferSepaDebit);
  const [stripeHasInitialized, setStripeHasInitialized] = React.useState(false);
  const availablePaymentTypes = new Set<server.dto.ClientPaymentMethodTypes>();

  availablePaymentTypes.add("card");

  if (props.config.payPerSepaAllowed) {
    availablePaymentTypes.add("sepaDebit");
  }

  if (props.config.payPerInvoiceAllowed) {
    availablePaymentTypes.add("invoice");
  }

  const createIntentMutation = useMutation(props.fetchIntentApi)

  React.useEffect(() => {
    if (props.config.payPerSepaAllowed && props.config.preferSepaDebit) {
      createSetupIntent("sepaDebit");
    } else {
      createSetupIntent("card");
    }
  }, []);

  const createSetupIntent = async (type: server.dto.ClientPaymentMethodTypes) => {
    setSelectedPaymentMethod(type);
    createIntentMutation.mutate(type);
  };

  const onSepaSelected = async () => {
    createSetupIntent("sepaDebit")
  }

  const onInvoiceSelected = async () => {
    setSelectedPaymentMethod("invoice");
  };

  const onCardSelected = async () => {
    setSelectedPaymentMethod("card");
    if (hasIgnoredCardWarning) {
      createSetupIntent("card");
    }
  };

  const onCardWarningIgnored = async () => {
    setHasIgnoredCardWarning(true);
    createSetupIntent("card");
  };

  const options = {
    clientSecret: createIntentMutation.data,
    fonts: [{ cssSrc: "https://fonts.googleapis.com/css?family=Inter" }],
    appearance: {
      variables: {
        fontFamily: "var(--font-family)",
        fontSizeBase: "14px",
        borderRadius: "var(--radius-large)",
        spacingUnit: "6px",
        colorPrimary: "#119BF5",
        colorText: "var(--text-color)",
        colorLogoTab: "dark"
      }
    }
  };

  const sepaAvailable = availablePaymentTypes.has("sepaDebit");
  const invoiceAvailable = availablePaymentTypes.has("invoice");
  const cardAvailable = availablePaymentTypes.has("card");

  const sepaDebitSelected = selectedPaymentMethod === "sepaDebit";
  const invoiceSelected = selectedPaymentMethod === "invoice";
  const cardSelected = selectedPaymentMethod === "card";

  const stripeSepaHeight = 546;
  const stripeCardHeight = 221;

  const sepaDebit = <PaymentMethodTabButton
    key="sepa"
    type="sepaDebit"
    onClick={onSepaSelected}
    isSelected={sepaDebitSelected}
  />;

  const card = <PaymentMethodTabButton
    key="card"
    type="card"
    onClick={onCardSelected}
    isSelected={cardSelected}
  />;

  const invoice = <PaymentMethodTabButton
    key="invoice"
    type="invoice"
    onClick={onInvoiceSelected}
    isSelected={invoiceSelected}
  />;

  let paymentMethods: Array<JSX.Element | boolean>
  if (props.config.preferSepaDebit) {
    paymentMethods = [sepaAvailable && sepaDebit, cardAvailable && card, invoiceAvailable && invoice];
  } else {
    paymentMethods = [cardAvailable && card, sepaAvailable && sepaDebit, invoiceAvailable && invoice];
  }

  const [paymentMethod1, paymentMethod2, paymentMethod3] = paymentMethods;

  if (createIntentMutation.isError) {
    return <SomethingUnexpectedHappenedMessageBox />
  }

  return (
    <>
      {availablePaymentTypes.size > 1 && <div className="flex margin-bottom-larger gap">
        {paymentMethod1}
        {paymentMethod2}
        {paymentMethod3}
      </div>}

      <Elements stripe={props.stripe} options={options} key={options.clientSecret}>
        {(createIntentMutation.isLoading || !stripeHasInitialized) && <Loading height={sepaDebitSelected ? stripeSepaHeight : stripeCardHeight} />}
        {invoiceSelected && <Invoice height={stripeCardHeight} />}

        {sepaDebitSelected && <Sepa
          stripe={props.stripe}
          options={options}
          returnUrl={props.config.stripeSetupIntentCompleteUrl}
          onHasLoaded={() => setStripeHasInitialized(true)}
          isLoading={!stripeHasInitialized}
        />}

        {cardSelected && <Card
          options={options}
          returnUrl={props.config.stripeSetupIntentCompleteUrl}
          hasIgnoredWarning={hasIgnoredCardWarning}
          onWarningIgnored={onCardWarningIgnored}
          onHasLoaded={() => setStripeHasInitialized(true)}
          isLoading={!stripeHasInitialized}
          height={stripeCardHeight}
        />}
      </Elements>
    </>
  );
};

export default StripeSetupIntentApp;
